import styled from 'styled-components';

// Palette and type mirror the v2 form-input library
// (src/common/formInputsV2/styles/tokens.js). Values are inlined rather than
// imported because this stylesheet is also consumed by the back-office screen
// (src/screens/BackOffice/part/BackofficeAncillariStepTwo.jsx) and should not
// pull a cross-directory dependency on the v2 lib.
const c = {
  primary: '#00a651',
  primaryRing: 'rgba(0, 166, 81, 0.16)',
  danger: '#d92d20',
  text: '#101828',
  textMuted: '#667085',
  textFaint: '#98a2b3',
  border: '#667085',
  borderStrong: '#344054',
  labelResting: '#1d2939',
  lockedLabel: '#475467',
  lockedSurface: '#f4f5f7',
  lockedBorder: 'rgba(16, 24, 40, 0.12)',
  white: '#ffffff',
};
const font = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const tablet = '900px';

export const GaranzieContainer = styled.div`
  width: 100%;
  max-width: 540px;
  margin: 0 auto;
  padding: 4px 10px;
  font-family: ${font};

  @media only screen and (max-width: ${tablet}) {
    padding: 4px 16px;
  }
`;

export const HeaderContainer = styled.div`
  margin-bottom: 18px;

  h3 {
    color: ${c.text};
    font-family: ${font};
    font-size: 20px;
    font-weight: 700;
    line-height: 1.35;
    margin-bottom: 0;
  }

  @media only screen and (max-width: ${tablet}) {
    h3 {
      font-size: 18px;
    }
  }
`;

export const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h4`
  color: ${c.text};
  font-family: ${font};
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  margin: 6px 0 4px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

export const GaranziaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: ${c.white};
  border-radius: 14px;
  border: 1.5px solid ${(props) => (props.hasError ? c.danger : c.border)};
  transition: border-color 120ms ease, box-shadow 120ms ease;

  /* Hover only when the row is actually toggleable */
  &:hover:not(:has(input:disabled)) {
    border-color: ${(props) => (props.hasError ? c.danger : c.borderStrong)};
  }

  /* Selected by the user: card stays white, crisp accent border + faint ring. */
  &:has(input:checked) {
    border-color: ${(props) => (props.hasError ? c.danger : c.primary)};
    box-shadow: 0 0 0 3px ${(props) => (props.hasError ? 'transparent' : c.primaryRing)};
  }

  /* Locked on — obbligatoria, or pulled in by another guarantee. Reads as
     settled / not-a-choice: soft grey fill and hairline, no accent, no ring. */
  &:has(input:checked:disabled) {
    background: ${c.lockedSurface};
    border-color: ${(props) => (props.hasError ? c.danger : c.lockedBorder)};
    box-shadow: none;
  }

  @media only screen and (max-width: ${tablet}) {
    padding: 12px 13px;
  }
`;

export const GaranziaHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

export const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;

  /* Custom checkbox — native control restyled in place (no markup change).
     Matches the v2 SelectableOption indicator: 20px, 6px radius, accent fill
     + drawn tick when checked. */
  input[type='checkbox'] {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin: 1px 0 0;
    appearance: none;
    -webkit-appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid ${c.border};
    border-radius: 6px;
    background: ${c.white};
    cursor: pointer;
    transition: background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease;

    &::after {
      content: '';
      width: 5px;
      height: 9px;
      margin-top: -1px;
      border: solid ${c.white};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      opacity: 0;
      transition: opacity 120ms ease;
    }

    &:hover:not(:disabled) {
      box-shadow: 0 0 0 4px rgba(102, 112, 133, 0.16);
    }

    &:checked {
      background: ${c.primary};
      border-color: ${c.primary};
    }

    &:checked::after {
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid ${c.primary};
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  label {
    flex: 1;
    margin: 0;
    cursor: pointer;
    font-family: ${font};
    font-size: 15px;
    font-weight: 400;
    color: ${c.labelResting};
    line-height: 1.4;

    @media only screen and (max-width: ${tablet}) {
      font-size: 14px;
    }

    &.disabled {
      cursor: not-allowed;
    }
  }

  /* Typographic hierarchy:
     unselected  -> resting weight, dark slate
     user-picked -> bold, near-black
     locked on   -> settled: medium weight, muted grey (not competing) */
  input:checked + label {
    font-weight: 600;
    color: ${c.text};
  }

  input:checked:disabled + label {
    font-weight: 500;
    color: ${c.lockedLabel};
  }
`;

export const ParameterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba(16, 24, 40, 0.08);

  label {
    font-family: ${font};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: ${c.textMuted};
  }
`;

export const CategorySection = styled.div`
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }
`;
